import React, {useEffect, useState} from 'react';
import {injectIntl} from 'react-intl';
import AutoSuggestField from '@FormComponents/AutoSuggestField';
import TextField from '@FormComponents/TextField';
import EmailFieldValidated from '@FormComponents/EmailFieldValidated';
import CheckboxField from '@FormComponents/CheckboxField';
import Button from '@FormComponents/Button';
import SelectField from '@FormComponents/SelectField';
import Icon from '@Components/Icon';
import Toast from '@Components/Toast';
import Joi from '@hapi/joi';
import {dataValidator} from 'dataValidator';
import axios from 'axios';
import Logger from '@ffw/logger-lib';

const JobAlertsForm = ({intl, keyword, color = 'bg-brand--blue'}) => {
  const initialState = {
    query: keyword ? keyword : '',
    location: '',
    email: '',
    distance: process.env.REACT_APP_DEFAULT_DISTANCE_VALUE,
    frequency: '',
    terms: false,
  };
  const [state, setState] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [locationOptions, setLocationOptions] = useState([]);
  const [toastState, setToastState] = useState(false);

  useEffect(() => {
    // Get email from userData.
    axios.post(`${process.env.REACT_APP_API_PREFIX}/get-callback`, {
      callback: '@Callbacks/getUserData',
      currentRoute: {},
      data: {},
    }).then((response) => {
      if (response?.data) {
        if (response.data.email) {
          setState((prev) => {
            return {
              ...prev,
              email: response.data.email,
            };
          });
        }
      }
    }).catch((error) => {});
  }, []);

  const queryValidation = {
    query: Joi.string()
        .required(),
  };

  const emailValidation = {
    email: Joi.string()
        .pattern(/^\w+([-+_.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/)
        .lowercase()
        .required(),
  };

  const locationValidation = {
    location: Joi.string()
        .required(),
  };

  const termsValidation = {
    terms: Joi.boolean()
        .required()
        .strict()
        .invalid(false),
  };

  const frequencyValidation = {
    frequency: Joi.string()
        .required(),
  };

  const distanceValidation = {
    distance: Joi.number()
        .required(),
  };

  const validationSchema = Joi.object().keys({
    ...queryValidation,
    ...emailValidation,
    ...locationValidation,
    ...termsValidation,
    ...frequencyValidation,
    ...distanceValidation,
  });

  validationSchema.errorOverrides = {
    query: {
      'any.required': intl.formatMessage({id: 'JobAlertsForm.KeywordError'}),
      'string.empty': intl.formatMessage({id: 'JobAlertsForm.KeywordError'}),
    },
    email: {
      'string.empty': intl.formatMessage({id: 'JobAlertsForm.EmailFieldRequired'}),
      'any.required': intl.formatMessage({id: 'JobAlertsForm.EmailFieldRequired'}),
      'string.pattern.base': intl.formatMessage({id: 'JobAlertsForm.EmailFieldInvalid'}),
    },
    location: {
      'any.required': intl.formatMessage({id: 'JobAlertsForm.LocationError'}),
      'string.empty': intl.formatMessage({id: 'JobAlertsForm.LocationError'}),
    },
    terms: {
      'any.invalid': intl.formatMessage({id: 'JobAlertsForm.TermsRequired'}),
      'any.empty': intl.formatMessage({id: 'JobAlertsForm.TermsRequired'}),
      'any.required': intl.formatMessage({id: 'JobAlertsForm.TermsRequired'}),
    },
    frequency: {
      'string.empty': intl.formatMessage({id: 'JobAlertsForm.FrequencyError'}),
      'any.required': intl.formatMessage({id: 'JobAlertsForm.FrequencyError'}),
      'any.invalid': intl.formatMessage({id: 'JobAlertsForm.FrequencyError'}),
    },
  };

  const onChange = (event) => {
    const name = event.target.name;
    let value = event.target.value;
    if (name === 'terms') {
      value = event.target.checked;
    }

    setState((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const onSubmit = (event) => {
    event.preventDefault();
    axios.post(`${process.env.REACT_APP_API_PREFIX}/get-callback`, {
      callback: '@Callbacks/validateLocation',
      currentRoute: {},
      data: state.location || '',
    }).then((res) => {
      let formErrors = null;
      if (!res.data || typeof res.data !== 'boolean') {
        formErrors = dataValidator({
          ...state,
          location: '',
        }, validationSchema);
      } else {
        formErrors = dataValidator(state, validationSchema);
      }
      if (formErrors) {
        setErrors(formErrors);
      } else {
        axios.post(`${process.env.REACT_APP_API_PREFIX}/get-callback`, {
          callback: '@Callbacks/jobAlertsSubmit',
          currentRoute: {},
          data: state,
        }).then((response) => {
          if (response.data) {
            setErrors({});
            setState(() => {
              return {
                ...initialState,
                email: state.email ? state.email : '',
              };
            });
            handleToast(true);
          }
        }).catch((errors) => {
          Logger.error(errors, 'JobAlertsForm');
        });
      }
    }).catch((errors) => {
      Logger.error(errors, 'JobAlertsForm');
    });
  };

  const handleToast = (isActive = false) => {
    setToastState(isActive);
  };

  const changeAutoComplete = (event) => {
    const value = event.target.value;
    setState((prev) => {
      return {
        ...prev,
        location: value,
      };
    });

    getAutocompleteOptions(value);
  };

  const getAutocompleteOptions = (value) => {
    axios.post(`${process.env.REACT_APP_API_PREFIX}/get-callback`, {
      callback: '@Callbacks/getLocationAutocomplete',
      currentRoute: {},
      data: {query: value},
    }).then((response) => {
      if (response.data) {
        setLocationOptions(response.data);
      }
    });
  };

  const frequencyOptions = [
    {
      value: intl.formatMessage({id: 'JobAlertsForm.FrequencyPlaceholder'}),
      label: 'JobAlertsForm.FrequencyPlaceholder',
      isTranslation: true,
    },
    {
      value: intl.formatMessage({id: 'JobAlertsForm.FrequencyDaily'}),
      label: 'JobAlertsForm.FrequencyDaily',
      isTranslation: true,
    },
    {
      value: intl.formatMessage({id: 'JobAlertsForm.FrequencyWeekly'}),
      label: 'JobAlertsForm.FrequencyWeekly',
      isTranslation: true,
    },
    {
      value: intl.formatMessage({id: 'JobAlertsForm.FrequencyMonthly'}),
      label: 'JobAlertsForm.FrequencyMonthly',
      isTranslation: true,
    },
  ];

  return (
    <>
      <div className="job-alerts">
        <div className={`banner cta-banner py-l text-white ${color} banner--job-alerts`}>
          <div className="wrapper l:flex justify-between items-center">
            <div className="cta-banner__media">
              <img src="/salary-app/assets/images/envelope--background-blue.svg" width="385" alt="envelope"/>
            </div>
            <div className="cta-banner__content content-block">
              <h2 className="content-block__title">{intl.formatMessage({id: 'JobAlertsForm.Title'})}</h2>
              <div className="content-block__description">{intl.formatMessage({id: 'JobAlertsForm.SubTitle'})}</div>
              <div className="content-block__form form-groups form-groups--row">
                <TextField
                  onChange={onChange}
                  settings={
                    {
                      state,
                      errors,
                      name: 'query',
                      placeholder: 'JobAlertsForm.JobTitle.Placeholder',
                    }
                  }/>
                <AutoSuggestField
                  onChange={changeAutoComplete}
                  settings={
                    {
                      state,
                      errors,
                      options: locationOptions,
                      name: 'location',
                      placeholder: 'JobAlertsForm.Location.Placeholder',
                    }
                  } />
              </div>
              <div className="content-block__form form-groups form-groups--row form-groups--remove-mobile-spacing">
                <EmailFieldValidated onChange={onChange} settings={{
                  name: 'email',
                  placeholder: 'JobAlertsForm.Email.Placeholder',
                  errors,
                  setErrors,
                  state,
                }}/>
                <SelectField onChange={onChange} settings={
                  {
                    state,
                    errors,
                    name: 'frequency',
                    options: frequencyOptions,
                  }
                } />
                <Button
                  className="button button--m button--icon button--full-width-to-icon button--filled button--off-white"
                  onClick={onSubmit} id="submitConsent">
                  <span className="button__text">
                    {intl.formatMessage({id: 'JobAlertsForm.Button'})}
                  </span>
                  <span className="icon" aria-hidden={true}>
                    <Icon type="arrow-right-30" />
                  </span>
                </Button>
              </div>
              <div className="content-block__form form-groups form-groups--row">
                <CheckboxField onChange={onChange} settings={
                  {
                    state,
                    errors,
                    labelAsMarkup: true,
                    label: 'JobAlertsForm.TermsLabel',
                    name: 'terms',
                  }
                }/>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Toast message={intl.formatMessage({id: 'JobAlertsForm.Toast'})} anchor="thank-you" show={toastState} onClose={handleToast} />
    </>
  );
};

export default injectIntl(JobAlertsForm);
